import type { ConfigurationSchema } from 'src/configuration.types'
import { $t } from 'src/plugins/i18n'
import {
  getPortfolioConfiguration,
  getPortfolioConfigurations,
  getPortfolioGroupMacro,
  getPortfolioRules,
} from 'src/services/PortfolioApiStore'
import type { ProxyRecord } from 'src/services/deserializers/deserializers.types'
import { getDependenciesFieldRules } from 'src/services/mappings/jsonSchema/jsonSchema.utils'
import type { ProductKind } from 'src/services/mappings/portfolio/portfolio.const'
import {
  INSURED_TYPE,
  ORDERED_PRODUCT_CATEGORIES,
  PORTFOLIO_CONFIGURATION_PRODUCT_GROUP,
  PRODUCT_CATEGORIES_BY_PARENT,
} from 'src/services/mappings/portfolio/portfolio.const'
import {
  GROUP_MACRO_COLORS,
  PRODUCT_GROUP_COLORS,
} from 'src/services/mappings/portfolio/portfolio.const2'
import type {
  PortfolioConfigurationGroupMacro,
  PortfolioConfigurationProductGroup,
} from 'src/services/mappings/portfolio/portfolio.types'
import { LEVERS } from 'src/services/request.const'
import type { FieldInterface } from 'src/tpbp-configuration/mapper/mappingInterfaces'
import { OWNERSHIP_CODES } from 'src/tpbp-configuration/models/models.const'
import { FEATURE_IDS } from 'src/tpbp-core/feature-flag/featureFlags.const'
import { hasFeature } from 'src/tpbp-core/feature-flag/featureFlags.utils'
import { FIELD_RULES } from 'src/tpbp-core/models/fields/fieldRules.const'
import type { EnumInterface, Scalar } from 'src/tpbp-core/models/global.type'
import { sortByNullAfter } from 'src/tpbp-core/utils/common.utils'
import { COLORS_BY_PRODUCT_KIND } from 'src/tpbp-json-api-handler/models/drawer-list-item.const'
import type {
  PersonModel,
  PortfolioConfigurationModel,
  PortfolioModel,
} from 'src/tpbp-json-api-handler/models/models'
import type { TailwindColor } from 'src/utils/getColor'
import { PRODUCT_COLORS } from 'src/utils/getColor.const'
import { logger } from 'src/utils/logger.utils'
import _ from 'underscore'
import type { CreationTypes } from '../relation/relation.const'
import { CREATION_TYPES } from '../relation/relation.const'
import { getPartner } from '../relation/relation.utils'

interface PRODUCT_CATEGORY {
  key: string
  order: number
}

interface PRODUCT_GROUP {
  key: string
  order: number
}

function getPortfolioProperty(record: ProxyRecord, originKey: string) {
  const rules = getPortfolioRules(record)
  if (!rules) {
    return false
  }

  return Object.hasOwn(rules, originKey) ? rules[originKey] : null
}

function getFieldStatus(record, originKey, fieldStatusesByOriginKey) {
  const rule = getPortfolioProperty(record, originKey)
  if (!rule) {
    return false
  }
  if (record.$disabledAttributes[originKey]) {
    return FIELD_RULES.isReadOnly
  }

  return rule.getFieldStatus(record, fieldStatusesByOriginKey)
}

export function portfolioIsReadOnly(
  record,
  originKey,
  fieldStatusesByOriginKey,
) {
  const status = getFieldStatus(record, originKey, fieldStatusesByOriginKey)

  return status === FIELD_RULES.isReadOnly
}

export function portfolioIsMandatory(
  record,
  originKey,
  fieldStatusesByOriginKey,
) {
  const status = getFieldStatus(record, originKey, fieldStatusesByOriginKey)

  return status === FIELD_RULES.isMandatory
}

export function portfolioIsOptional(
  record: PortfolioModel,
  originKey,
  fieldStatusesByOriginKey,
) {
  if (originKey === LEVERS.beneficiaryClause) {
    const configuration = getPortfolioConfiguration(record.productKind)
    const isPresent =
      configuration?.dependentObjects?.beneficiary_clause?.presence || false

    return isPresent
  }
  const status = getFieldStatus(record, originKey, fieldStatusesByOriginKey)

  return status === FIELD_RULES.isOptional
}

export function isBankNamePresent(record: PortfolioModel) {
  const configuration = getPortfolioConfiguration(record.productKind)
  const isPresent = configuration?.dependentObjects?.banks?.presence || false
  return isPresent
}

export function portfolioHasSingleOwner(record: PortfolioModel) {
  const configuration = getPortfolioConfiguration(record.productKind)
  return configuration?.hasSingleOwner || false
}

export const getPortfolioProductKindEnumsFromGroupMacroAndCategory = _.memoize(
  _getPortfolioProductKindEnumsFromGroupMacroAndCategory,
  (groupMacro, category) => `${groupMacro}-${category}`,
)

function _getPortfolioProductKindEnumsFromGroupMacroAndCategory(
  groupMacro,
  category,
): EnumInterface[] {
  const portfolioConfigurations = getPortfolioConfigurations()

  return portfolioConfigurations
    .filter(
      ({ productGroupMacro, productCategory }) =>
        productGroupMacro === groupMacro && productCategory === category,
    )
    .map(({ productKind, productKindLabel }) => ({
      key: productKind,
      label: productKindLabel,
    }))
}

export const getPortfolioProductKindEnumsFromCategory = _.memoize(
  (category) => {
    const portfolioConfigurations = getPortfolioConfigurations()
    const groupConfigurations = portfolioConfigurations.filter(
      ({ productCategory, hidden }) => productCategory === category && !hidden,
    )

    return _getPortfolioProductKindEnumsFromConfigurations(groupConfigurations)
  },
)

export const getPortfolioProductKindEnumsFromGroup = _.memoize((group) => {
  const portfolioConfigurations = getPortfolioConfigurations()
  const groupConfigurations = portfolioConfigurations.filter(
    ({ productGroup, hidden }) => productGroup === group && !hidden,
  )

  return _getPortfolioProductKindEnumsFromConfigurations(groupConfigurations)
})

export const getPortfolioProductKindEnumsFromGroupMacro = _.memoize(
  (groupMacro) => {
    const portfolioConfigurations = getPortfolioConfigurations()
    const groupMacroConfigurations = portfolioConfigurations.filter(
      ({ productGroupMacro, hidden }) =>
        productGroupMacro === groupMacro && !hidden,
    )

    return _getPortfolioProductKindEnumsFromConfigurations(
      groupMacroConfigurations,
    )
  },
)

function _getPortfolioProductKindEnumsFromConfigurations(
  configurations: PortfolioConfigurationModel[],
): EnumInterface[] {
  return configurations
    .sort(
      (configA, configB) => configA.productKindOrder - configB.productKindOrder,
    )
    .map(({ productKind, productKindLabel }) => {
      return {
        key: productKind,
        label: productKindLabel,
      }
    }) satisfies EnumInterface[]
}

export function getPortfolioProductKindEnums(
  record: PortfolioModel | Record<string, unknown>,
) {
  if (hasFeature(FEATURE_IDS.evolMvp3)) {
    if (record.productCategory)
      return getPortfolioProductKindEnumsFromCategory(record.productCategory)
    if (record.productGroup)
      return getPortfolioProductKindEnumsFromGroup(record.productGroup)
    if (record.productGroupFromProductKind)
      return getPortfolioProductKindEnumsFromGroup(
        record.productGroupFromProductKind,
      )
    if (record.productGroupMacro)
      return getPortfolioProductKindEnumsFromGroupMacro(
        record.productGroupMacro,
      )
  }
  const unsortedList = getUnsortedPortfolioProductKindEnums(record)
  return _.sortBy(unsortedList, 'label')
}

export function getUnsortedPortfolioProductKindEnums(
  record: PortfolioModel | Record<string, unknown>,
) {
  const productCategory = record.productCategory || record.productCategoryNew
  if (record.productGroupMacro && productCategory) {
    return getPortfolioProductKindEnumsFromGroupMacroAndCategory(
      record.productGroupMacro,
      productCategory,
    )
  }
  const portfolioConfigurations = getPortfolioConfigurations()

  const list = portfolioConfigurations
    .filter((configuration) => {
      if (productCategory) {
        return configuration.productCategory === productCategory
      }

      return true
    })
    .map<EnumInterface>(({ productKind: key, productKindLabel: label }) => ({
      key,
      label,
    }))

  return list
}

export const getProductCategoryEnumLabel = _.memoize(
  (productCategory: Scalar): string => {
    const portfolioConfigurations = getPortfolioConfigurations()
    const portfolioConfiguration = portfolioConfigurations.find(
      (configuration) => configuration.productCategory === productCategory,
    )

    return (
      portfolioConfiguration?.productCategoryLabel ?? productCategory.toString()
    )
  },
)

export const getProductGroupEnumLabel = _.memoize(
  (productGroup: Scalar): string => {
    const portfolioConfigurations = getPortfolioConfigurations()
    const portfolioConfiguration = portfolioConfigurations.find(
      (configuration) => configuration.productGroup === productGroup,
    )
    return portfolioConfiguration?.productGroupLabel ?? productGroup.toString()
  },
)

export const computeProductKindLabel = _.memoize(
  (productKind: string): string => {
    const portfolioConfigurations = getPortfolioConfigurations()
    const portfolioConfiguration = portfolioConfigurations.find(
      (configuration) => configuration.productKind === productKind,
    )
    return portfolioConfiguration?.productKindLabel ?? productKind
  },
)

// TODO fix record type (remove Record<string, unknown>)
export const getPortfolioProductKindEnumLabel = _.memoize(
  (
    productKind: Scalar,
    record?: PortfolioModel | Record<string, unknown>,
  ): string => {
    if (hasFeature(FEATURE_IDS.evolMvp3)) return productKind.toString()
    const portfolioConfigurations = getPortfolioConfigurations()
    const productCategory =
      record?.productCategory ?? record?.productCategoryNew

    const portfolioConfiguration = portfolioConfigurations
      .filter((configuration) => {
        if (productCategory) {
          return configuration.productCategory === productCategory
        }

        return true
      })
      .find((configuration) => configuration.productKind === productKind)

    return (
      portfolioConfiguration?.productKindLabel ??
      (typeof productKind === 'string' ? productKind : productKind.toString())
    )
  },
)

export function getProductCategoriesFromProductGroupMacro(groupMacro: string) {
  const portfolioConfigurations = getPortfolioConfigurations()
  const productCategories = portfolioConfigurations
    .filter(({ productGroupMacro }) => productGroupMacro === groupMacro)
    .map(({ productCategory }) => productCategory)

  return _.uniq(productCategories)
}

export function getProductGroupsFromProductGroupMacro(groupMacro: string) {
  const portfolioConfigurations = getPortfolioConfigurations()
  const productGroups = portfolioConfigurations
    .filter(({ productGroupMacro }) => productGroupMacro === groupMacro)
    .sort(
      (configA, configB) =>
        configA.productGroupOrder - configB.productGroupOrder,
    )
    .map(({ productGroup }) => productGroup)

  return _.uniq(productGroups)
}

export const getGroupMacroLabel = _.memoize((groupMacro: string) => {
  const portfolioConfigurations = getPortfolioConfigurations()
  const portfolioConfiguration = portfolioConfigurations.find(
    ({ productGroupMacro }) => productGroupMacro === groupMacro,
  )

  if (portfolioConfiguration === undefined) {
    logger.warn(`Unknown groupMacro '${groupMacro}'`)

    return ''
  }
  return portfolioConfiguration.productGroupMacroLabel
})

export const getProductGroupLabel = _.memoize((productGroup: string) => {
  const portfolioConfigurations = getPortfolioConfigurations()
  const portfolioConfiguration = portfolioConfigurations.find(
    ({ productGroup: group }) => group === productGroup,
  )

  if (portfolioConfiguration === undefined) {
    logger.warn(`Unknown productCategory '${productGroup}'`)

    return ''
  }
  return portfolioConfiguration.productGroupLabel
})

export function getGroupMacroColor(
  groupMacro: PortfolioConfigurationGroupMacro,
  defaultColor: TailwindColor = 'slate-200',
) {
  if (GROUP_MACRO_COLORS[groupMacro] === undefined) {
    logger.warn(`Unknown color for groupMacro '${groupMacro}'`)
  }
  return GROUP_MACRO_COLORS[groupMacro] ?? defaultColor
}

export function getProductGroupColor(
  productGroup: PortfolioConfigurationProductGroup,
  defaultColor: TailwindColor = 'slate-200',
) {
  if (PRODUCT_GROUP_COLORS[productGroup] === undefined) {
    logger.warn(`Unknown color for productGroup '${productGroup}'`)
  }
  return PRODUCT_GROUP_COLORS[productGroup] ?? defaultColor
}

export function getProductKindColor(productKind: ProductKind): TailwindColor {
  return COLORS_BY_PRODUCT_KIND[productKind] ?? PRODUCT_COLORS.default
}

export function getPortfolioColor(portfolio: PortfolioModel) {
  const macroGroup = getPortfolioGroupMacro(portfolio.productKind)

  if (!macroGroup) return

  return getGroupMacroColor(macroGroup)
}

export function getPortfolioProductCategories(
  record: PortfolioModel,
): Scalar[] {
  const { productGroupMacro, productCategoryParent } = record

  if (productGroupMacro) {
    return getProductCategoriesFromProductGroupMacro(productGroupMacro)
  }

  return Object.hasOwn(PRODUCT_CATEGORIES_BY_PARENT, productCategoryParent)
    ? PRODUCT_CATEGORIES_BY_PARENT[productCategoryParent] ?? []
    : getDistinctProductCategory()
}

export function getPortfolioProductGroups(record?: PortfolioModel) {
  if (record === undefined) {
    if (!hasFeature(FEATURE_IDS.evolMvp3))
      return Object.values(PORTFOLIO_CONFIGURATION_PRODUCT_GROUP)
    return getDistinctProductGroups()
  }

  const { productGroupMacro } = record
  if (productGroupMacro === undefined) {
    return getDistinctProductGroups()
  }
  return getProductGroupsFromProductGroupMacro(productGroupMacro)
}

export function getOwnerNewEnums(
  _record: PortfolioModel,
  currentUser: PersonModel,
): EnumInterface[] {
  if (!currentUser) return []
  const owners = [
    {
      key: 'user',
      label: `${currentUser.firstName} ${currentUser.lastName}`,
    },
  ]

  const partner = getPartner(currentUser)
  if (partner) {
    owners.push({
      key: 'partner',
      label: `${partner.firstName} ${partner.lastName}`,
    })
    owners.unshift({
      key: OWNERSHIP_CODES.common,
      label: $t('budget.scope.Common'),
    })
  }

  return owners
}

const ownerCreationTypes = new Set<CreationTypes>([
  CREATION_TYPES.currentUser,
  CREATION_TYPES.partner,
  CREATION_TYPES.minor,
])

export function getOwner(record: PortfolioModel) {
  const { _holders } = record
  const owners = _holders.filter((holder) =>
    ownerCreationTypes.has(holder.creationType as CreationTypes),
  )

  if (owners.length > 1) return $t('budget.scope.Common')
  return owners[0] ? `${owners[0].firstName} ${owners[0].lastName}` : ''
}

export function filterSchemaEnums(
  schemaEnums: Scalar[],
  listToRemove: Scalar[],
) {
  return schemaEnums.filter((schemaEnum) => !listToRemove.includes(schemaEnum))
}

export function forceReevaluateProductGroup(record: PortfolioModel) {
  // eslint-disable-next-line no-unused-expressions
  record.productGroupFromProductKind // I don't knwon why but we need to evalute this field (model expression) for it to work on some cases
}

export function getPortfolioEnums(
  record: PortfolioModel,
  field: FieldInterface,
) {
  if (field.key === 'productKind') throw new Error('Should not occur')
  if (field.key === 'productCategoryNew') {
    return getPortfolioProductCategories(record)
  }
  if (field.key === 'productGroup') {
    if (!record[field.key]) {
      forceReevaluateProductGroup(record)
    }
    return getPortfolioProductGroups(record)
  }
  if (field.key === 'productGroupFromProductKind')
    return getPortfolioProductGroups(record)

  const portfolioConfiguration = record.productKind
    ? getPortfolioConfiguration(record.productKind)
    : null

  if (portfolioConfiguration && field.key === 'bankName') {
    return (
      (portfolioConfiguration?.dependentObjects?.banks
        ?.bank_names as unknown as Scalar[]) ?? []
    )
  }

  const schema =
    portfolioConfiguration?.schema as unknown as ConfigurationSchema
  const schemaEnums = schema
    ? getPortfolioEnumsFromConfigurationSchema(record, field, schema)
    : null

  if (schemaEnums && field.key === 'insuredType')
    return filterSchemaEnums(schemaEnums, [
      INSURED_TYPE.jointFirstToDie,
      INSURED_TYPE.jointSecondToDie,
    ])
  if (schemaEnums) return schemaEnums
  if (field.enumKeys) return field.enumKeys

  throw new Error(`Unknown portfolio mapping case '${field.key}'`)
}

export function getPortfolioEnumsFromConfigurationSchema(
  record: PortfolioModel,
  field: FieldInterface,
  schema: ConfigurationSchema,
): Scalar[] | undefined {
  const { originKey } = field
  const fieldRules = getDependenciesFieldRules(schema.dependencies, record)
  const fieldRule = fieldRules?.[originKey]
  if (fieldRule?.enum) return fieldRule.enum
  const property = schema.properties?.[originKey]
  return property?.enum
}

export function getProductCategory(productKind) {
  const configuration = getPortfolioConfiguration(productKind)
  return configuration?.productCategory ?? ''
}

export function getDistinctProductCategory() {
  const configurations = getPortfolioConfigurations()
  const productCategoriesByKey: Record<string, PRODUCT_CATEGORY> =
    configurations.reduce((productCategoryByKey, configuration) => {
      const { productCategory } = configuration
      if (!Object.hasOwn(productCategoryByKey, productCategory)) {
        const order = ORDERED_PRODUCT_CATEGORIES[productCategory] || null
        productCategoryByKey[productCategory] = {
          key: productCategory,
          order,
        }
      }

      return productCategoryByKey
    }, {})

  const list = Object.values(productCategoriesByKey)
  const sorted = sortByNullAfter(list, 'order')

  return sorted.map((row) => row.key)
}

export function getDistinctProductGroups() {
  const configurations = getPortfolioConfigurations()
  const productGroupsByKey: Record<string, PRODUCT_GROUP> =
    configurations.reduce((productGroupByKey, configuration) => {
      const { productGroup, productGroupOrder } = configuration
      if (
        !Object.hasOwn(productGroupByKey, productGroup) &&
        productGroup !== PORTFOLIO_CONFIGURATION_PRODUCT_GROUP.nonDefini
      ) {
        const order = productGroupOrder ?? null
        productGroupByKey[productGroup] = {
          key: productGroup,
          order,
        }
      }

      return productGroupByKey
    }, {})

  const list = Object.values(productGroupsByKey)
  const sorted = sortByNullAfter(list, 'order')

  return sorted.map((row) => row.key)
}
